/*
 * Design: Swiss Precision × 和の余白
 * SaaS導入の課題を提示し、Antigravity × MCPによる解決への橋渡しをするセクション
 * Mobile: カード余白最適化、グリッド調整、CTAバナーのレスポンシブ対応
 */

import { motion } from "framer-motion";
import { TrendingDown, Eye, Layers, RefreshCw, DollarSign, Users } from "lucide-react";

const SAAS_CHALLENGES = [
  {
    icon: TrendingDown,
    stat: "70%",
    title: "定着しない",
    description:
      "入れたSaaSが現場に浸透せず、結局Excelや紙に戻ってしまう。操作が複雑で、日常業務に組み込めないまま放置されるケースが後を絶ちません。",
  },
  {
    icon: Eye,
    stat: "ROI?",
    title: "投資対効果が見えない",
    description:
      "月額費用は発生し続けるのに、業務がどれだけ改善されたか定量的に測れない。経営層への説明に困り、次の投資判断ができません。",
  },
  {
    icon: Layers,
    stat: "20%",
    title: "機能を使い切れていない",
    description:
      "多機能なSaaSを入れても、実際に使っているのは全体の2割程度。高い月額費用を払いながら、基本機能しか活用できていません。",
  },
  {
    icon: RefreshCw,
    stat: "分断",
    title: "ツール間の連携ができない",
    description:
      "部署ごとにバラバラなSaaSを入れた結果、データがサイロ化。手作業でのデータ転記が新たな業務負担を生んでいます。",
  },
  {
    icon: DollarSign,
    stat: "膨張",
    title: "コストが積み上がる",
    description:
      "ユーザー数×月額の課金モデルにより、組織拡大とともにコストが膨張。使っていないアカウントにも費用が発生し続けます。",
  },
  {
    icon: Users,
    stat: "属人",
    title: "管理者に依存する",
    description:
      "設定・カスタマイズ・トラブル対応が特定の担当者に集中。その人が異動・退職すると、運用が止まるリスクを抱えています。",
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export function SaaSChallengeSection() {
  return (
    <section id="saas-challenges" className="py-14 sm:py-20 lg:py-28 bg-[#F9F9F9]">
      <div className="container">
        {/* Section Header */}
        <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-16">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
            <div className="w-1.5 sm:w-2 h-7 sm:h-8 bg-[#E03030]" />
            <span
              className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              SaaS Challenges
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
            SaaS、こんな壁にぶつかっていませんか？
          </h2>
          <p className="text-sm sm:text-base text-[#333333] max-w-3xl leading-relaxed">
            便利なはずのSaaSが、現場では「使われないツール」になっている——。
            多くの企業が抱えるこの課題は、ツールの問題ではなく「活用の仕組み」の問題です。
          </p>
        </motion.div>

        {/* Challenge Cards - 2 cols on mobile, 3 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {SAAS_CHALLENGES.map((challenge, i) => (
            <motion.div
              key={challenge.title}
              {...fadeInUp}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative bg-white border border-[#E0E0E0] p-4 sm:p-7 hover:border-[#E03030] transition-all duration-300 hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
            >
              {/* Stat Badge */}
              <div className="absolute top-3 sm:top-5 right-3 sm:right-5">
                <span
                  className="text-lg sm:text-2xl font-extrabold text-[#E03030]/15 group-hover:text-[#E03030]/30 transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {challenge.stat}
                </span>
              </div>

              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#FEF2F2] text-[#E03030] rounded-sm mb-3 sm:mb-4">
                <challenge.icon size={16} strokeWidth={1.5} className="sm:hidden" />
                <challenge.icon size={20} strokeWidth={1.5} className="hidden sm:block" />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-[#1A1A1A] mb-1.5 sm:mb-2 group-hover:text-[#E03030] transition-colors">
                {challenge.title}
              </h3>
              <p className="text-[11px] sm:text-sm text-[#333333] leading-relaxed line-clamp-4 sm:line-clamp-none">
                {challenge.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Transition Message */}
        <motion.div
          {...fadeInUp}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-8 sm:mt-14 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 px-5 sm:px-8 py-3 sm:py-4 bg-[#1A1A1A] text-white rounded-sm">
            <span className="text-xs sm:text-sm font-medium">
              これらの課題を根本から解決するのが
            </span>
            <span className="text-sm sm:text-base font-bold text-[#E03030]">
              Antigravity × Workspace MCP
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
